import React from 'react';
import ReactDOM from 'react-dom/client';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { ParsleeAuthProvider } from './auth/ParsleeAuthProvider';
import { ProtectedRoute } from './auth/ProtectedRoute';
import { StaffGate } from './auth/StaffGate';
import App from './App';
import BillingPage from './components/BillingPage';
import AdminDashboard from './admin/AdminDashboard';
import OrgDetailPage from './admin/OrgDetailPage';
import SubscriptionDetailPage from './admin/SubscriptionDetailPage';
import RatePlanDetailPage from './admin/RatePlanDetailPage';
import WelcomePage from './welcome/WelcomePage';
import GettingStartedPage from './welcome/GettingStartedPage';
// shadcn/Tailwind base (U8). Imported FIRST so the unlayered platform-nav /
// platform-ui / App.css rules below win on any conflict — this keeps Tailwind's
// preflight base reset from bleeding into the existing Fluent UI + platform-nav
// surfaces (equal-specificity rules, last one in source order wins).
import './styles/globals.css';
import '@mayline-ai/platform-nav/styles.css';
import '@mayline-ai/platform-ui/styles.css';
import './styles/App.css';

// Error boundary to catch and display React render crashes
class ErrorBoundary extends React.Component<
  { children: React.ReactNode },
  { error: Error | null }
> {
  constructor(props: { children: React.ReactNode }) {
    super(props);
    this.state = { error: null };
  }
  static getDerivedStateFromError(error: Error) {
    return { error };
  }
  componentDidCatch(error: Error, info: React.ErrorInfo) {
    console.error('[Parslee] React crash:', error, info.componentStack);
  }
  render() {
    if (this.state.error) {
      return (
        <div style={{ padding: 40, fontFamily: 'monospace' }}>
          <h1 style={{ color: 'red' }}>React Error</h1>
          <pre style={{ whiteSpace: 'pre-wrap', background: '#f5f5f5', padding: 16 }}>
            {this.state.error.message}
          </pre>
          <pre style={{ whiteSpace: 'pre-wrap', fontSize: 12, color: '#666' }}>
            {this.state.error.stack}
          </pre>
        </div>
      );
    }
    return this.props.children;
  }
}

const root = ReactDOM.createRoot(
  document.getElementById('root') as HTMLElement
);

// Debug: log to confirm JS executes
console.log('[Parslee] index.tsx executing, pathname:', window.location.pathname);

// The admin dashboard is Parslee-staff-only: every internal route sits behind
// ProtectedRoute (session auth) + StaffGate (Parslee-staff authorization). The
// customer-facing marketplace/consent flows (/welcome, /getting-started) are
// reached by non-staff customers with no session, so they stay ungated.
//
// The host root lands on the platform admin view (/admin); the org-level
// AI Employee Dashboard lives at /overview and the two link to each other.
const Gated = ({ children }: { children: React.ReactNode }) => (
  <ProtectedRoute>
    <StaffGate>{children}</StaffGate>
  </ProtectedRoute>
);

root.render(
  <React.StrictMode>
    <ErrorBoundary>
      <ParsleeAuthProvider>
        <BrowserRouter>
          <Routes>
            <Route path="/" element={<Navigate to="/admin" replace />} />
            <Route path="/overview" element={<Gated><App /></Gated>} />
            <Route path="/billing" element={<Gated><BillingPage /></Gated>} />
            <Route path="/admin" element={<Gated><AdminDashboard /></Gated>} />
            <Route path="/admin/orgs/:orgId" element={<Gated><OrgDetailPage /></Gated>} />
            <Route path="/admin/subscriptions/:subId" element={<Gated><SubscriptionDetailPage /></Gated>} />
            <Route path="/admin/rate-plans/:planId" element={<Gated><RatePlanDetailPage /></Gated>} />
            <Route path="/welcome" element={<WelcomePage />} />
            <Route path="/getting-started" element={<GettingStartedPage />} />
          </Routes>
        </BrowserRouter>
      </ParsleeAuthProvider>
    </ErrorBoundary>
  </React.StrictMode>
);
